import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Input } from './ui/input';
import { Package, Search, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ORANGE = '#ee7203';
// Same tinted, inset look as every other typable box on the quote.
const FIELD_CLS = 'rounded-xl bg-orange-50 border border-orange-200 shadow-inner focus-visible:bg-white focus-visible:border-orange-400 focus-visible:ring-2 focus-visible:ring-orange-200';
const ROW_COLUMNS = '28px 150px minmax(0, 1fr) auto';

const codeOf = (p) => String((p && (p.product_code || p.code)) || '').trim().toUpperCase();

/**
 * "Add Product Group" window for the Create Quote modal. Type the start of a product code (a prefix, e.g. 25YJ) to list every
 * product whose code starts with it, tick as many lines as you want (or Select all), then add them all to the quote in one go.
 * Ticks are kept while you search other prefixes, so a group can be built from more than one prefix.
 * Uses the same strict "code starts with" search as the Code box on a quote line.
 */
const ProductGroupModal = ({ isOpen, onClose, onAdd }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({}); // { CODE: product } - kept across searches
  const searchSeqRef = useRef(0);
  const searchTimerRef = useRef(null);
  const inputRef = useRef(null);

  // Fresh every time it opens.
  useEffect(() => {
    if (!isOpen) return undefined;
    setQuery('');
    setResults([]);
    setSelected({});
    setLoading(false);
    const focusTimer = setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 60);
    return () => clearTimeout(focusTimer);
  }, [isOpen]);

  // Escape closes.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchSeqRef.current += 1;
  }, []);

  // Each search gets a number and only the newest one may show its results (same protection as the Code box).
  const runSearch = async (text) => {
    const seq = ++searchSeqRef.current;
    try {
      const res = await api.get(`${API}/api/stock-database/search-code`, { params: { q: text.trim(), limit: 300, include_all: true } });
      if (seq !== searchSeqRef.current) return;
      setResults(res.data?.results || []);
    } catch (error) {
      if (seq !== searchSeqRef.current) return;
      console.error('Product group search failed:', error);
      setResults([]);
    } finally {
      if (seq === searchSeqRef.current) setLoading(false);
    }
  };

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.trim().length < 2) {
      searchSeqRef.current += 1; // cancels any search still on its way back
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    searchTimerRef.current = setTimeout(() => runSearch(value), 250);
  };

  const toggleOne = (product) => {
    const key = codeOf(product);
    if (!key) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key]; else next[key] = product;
      return next;
    });
  };

  const allShownSelected = results.length > 0 && results.every((p) => selected[codeOf(p)]);
  const toggleAllShown = () => {
    setSelected((prev) => {
      const next = { ...prev };
      if (allShownSelected) results.forEach((p) => { delete next[codeOf(p)]; });
      else results.forEach((p) => { if (codeOf(p)) next[codeOf(p)] = p; });
      return next;
    });
  };

  const selectedCount = Object.keys(selected).length;
  const addSelected = () => {
    if (selectedCount === 0) return;
    onAdd?.(Object.values(selected));
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-4"
      style={{ zIndex: 120 }}
      onClick={onClose}
      data-testid="product-group-overlay"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl border-2 border-orange-300 w-full flex flex-col overflow-hidden"
        style={{ maxWidth: 960, height: 'min(86vh, 760px)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="product-group-modal"
      >
        {/* Header: title on the left, the orange Add button always visible before the red X */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-orange-100 bg-white flex-shrink-0 flex-nowrap">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
          >
            <Package className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-extrabold text-gray-900 leading-tight truncate">Add Product Group</h2>
            <p className="text-[11px] text-gray-500 truncate">Search a code prefix, tick the lines you want, add them all to the quote</p>
          </div>
          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            <button
              type="button"
              onClick={addSelected}
              disabled={selectedCount === 0}
              className="inline-flex items-center gap-1.5 rounded-xl h-9 px-4 border-2 border-orange-500 text-white text-xs font-bold whitespace-nowrap shadow-sm hover:shadow-md active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: ORANGE }}
              data-testid="product-group-add-btn"
            >
              Add {selectedCount} product{selectedCount === 1 ? '' : 's'} to quote
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-md transition-colors"
              title="Close"
              data-testid="product-group-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 pb-3 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              ref={inputRef}
              value={query}
              onChange={handleQueryChange}
              placeholder="Type the start of a product code, e.g. 25YJ"
              className={`pl-9 h-10 text-sm ${FIELD_CLS}`}
              data-testid="product-group-search"
            />
            {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" style={{ color: ORANGE }} />}
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-gray-500 min-h-[20px]">
            {results.length > 0 ? (
              <label className="inline-flex items-center gap-2 cursor-pointer font-semibold text-gray-700" data-testid="product-group-select-all">
                <input type="checkbox" checked={allShownSelected} onChange={toggleAllShown} className="accent-orange-500 w-4 h-4" />
                Select all {results.length} shown
              </label>
            ) : <span />}
            <span>
              {selectedCount > 0 ? `${selectedCount} ticked` : ''}
            </span>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5" data-testid="product-group-results">
          {query.trim().length < 2 ? (
            <div className="text-center py-16 text-gray-400">
              <Package className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-sm">Type at least 2 characters of a product code to list every product that starts with it.</p>
            </div>
          ) : !loading && results.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-sm">No products have a code starting with “{query.trim()}”.</p>
            </div>
          ) : (
            <div className="rounded-xl border-2 border-orange-200 overflow-hidden bg-white">
              {results.map((p, idx) => {
                const key = codeOf(p);
                const checked = !!selected[key];
                const onHand = p.on_hand_quantity ?? p.on_hand_balance ?? 0;
                const free = p.free_stock ?? 0;
                const onOrder = p.on_order_quantity ?? 0;
                return (
                  <div
                    key={`${key}-${idx}`}
                    onClick={() => toggleOne(p)}
                    className={`grid items-center gap-x-4 px-4 py-2.5 cursor-pointer transition-colors ${idx > 0 ? 'border-t border-gray-100' : ''} ${checked ? 'bg-orange-50' : 'hover:bg-orange-50/50'}`}
                    style={{ gridTemplateColumns: ROW_COLUMNS }}
                    data-testid={`product-group-row-${key}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOne(p)}
                      onClick={(e) => e.stopPropagation()}
                      className="accent-orange-500 w-4 h-4"
                    />
                    <span className="font-mono font-bold text-sm text-gray-900 break-all">{p.product_code || p.code}</span>
                    <span className="text-sm text-gray-800 min-w-0">{p.description || p.name}</span>
                    <span className="flex items-center gap-4 text-xs whitespace-nowrap">
                      <span className={onHand > 0 ? 'text-green-600' : 'text-gray-400'}><span className="text-gray-400">Stock:</span> {onHand}</span>
                      <span className={free > 0 ? 'text-blue-600' : 'text-gray-400'}><span className="text-gray-400">Free:</span> {free}</span>
                      <span className={onOrder > 0 ? 'text-yellow-600' : 'text-gray-400'}><span className="text-gray-400">Order:</span> {onOrder}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductGroupModal;
